<template>
  <el-dialog
    :model-value="visible"
    :title="`${envData?.id ? '编辑' : '新增'}环境`"
    width="600px"
    @update:model-value="$emit('update:visible', $event)"
  >
    <el-form :model="envForm" label-position="top" size="large">
      <el-form-item
        label="环境名称（中文）"
        :error="envFormErrors.name"
        required
      >
        <el-input
          v-model="envForm.name"
          placeholder="请输入环境名称（中文）"
          :disabled="submitting || envData?.id"
        />
      </el-form-item>
      <el-form-item
        label="环境名称（英文）"
        :error="envFormErrors.eName"
        required
      >
        <el-input
          v-model="envForm.eName"
          placeholder="请输入环境名称（英文），构建镜像和创建目录都会依赖这个"
          :disabled="submitting || envData?.id"
        />
      </el-form-item>
      <el-form-item label="默认分支" :error="envFormErrors.branch" required>
        <el-input
          v-model="envForm.branch"
          placeholder="请输入该环境下项目使用的默认分支"
          :disabled="submitting"
        />
      </el-form-item>
      <el-form-item label="环境变量">
        <template #label>
          环境变量：
          <el-button
            type="primary"
            size="small"
            :icon="Plus"
            @click="addEnvMapItem"
            :disabled="submitting"
          >
            添加环境变量
          </el-button>
        </template>
        <div class="space-y-3">
          <div
            v-for="(item, index) in envForm.envMaps"
            :key="index"
            class="flex items-center mb-[4px]"
          >
            <el-input
              v-model="item.key"
              placeholder="变量名"
              :disabled="submitting"
              class="flex-1"
              size="small"
            />
            <el-divider direction="vertical" />
            <el-input
              v-model="item.value"
              placeholder="变量值"
              :disabled="submitting"
              class="flex-1"
              size="small"
            />
            <el-divider direction="vertical" />
            <el-button
              type="danger"
              size="small"
              :icon="Delete"
              @click="envForm.envMaps.splice(index, 1)"
              :disabled="submitting || envForm.envMaps.length <= 1"
            ></el-button>
          </div>
        </div>
      </el-form-item>
    </el-form>
    <template #footer>
      <span class="dialog-footer">
        <el-button
          @click="$emit('update:visible', false)"
          :disabled="submitting"
          >取消</el-button
        >
        <el-button type="primary" @click="handleConfirm" :loading="submitting">
          {{ submitting ? "提交中..." : "保存" }}
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { reactive, ref, watch } from "vue";
import { Delete, Plus } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import { addEnvironment, updateEnvironment } from "../api/envApi";
import type { Environment, EnvironmentForm } from "../types";

const props = defineProps<{
  visible: boolean;
  envData?: Environment;
}>();

const emit = defineEmits<{
  (e: "update:visible", value: boolean): void;
  (e: "success"): void;
}>();

const submitting = ref(false);

const envForm = reactive<EnvironmentForm>({
  name: "",
  eName: "",
  branch: "",
  envMaps: [{ key: "", value: "" }],
});

watch(
  () => props.visible,
  (newVal) => {
    if (newVal && props.envData) {
      envForm.name = props.envData.name;
      envForm.eName = props.envData.eName;
      envForm.branch = props.envData.branch;
      envForm.envMaps = props.envData.envMaps;
    } else {
      envForm.name = "";
      envForm.eName = "";
      envForm.branch = "";
      envForm.envMaps = [{ key: "", value: "" }];
    }
  },
  {
    immediate: true,
  },
);

const envFormErrors = reactive<Record<string, string>>({
  name: "",
  eName: "",
  branch: "",
});

const addEnvMapItem = () => {
  envForm.envMaps.push({ key: "", value: "" });
};

const validateEnvForm = () => {
  let isValid = true;
  envFormErrors.name = "";
  envFormErrors.eName = "";
  envFormErrors.branch = "";

  if (!envForm.name) {
    envFormErrors.name = "请输入环境名称（中文）";
    isValid = false;
  }
  if (!envForm.eName) {
    envFormErrors.eName = "请输入环境名称（英文）";
    isValid = false;
  }
  if (!envForm.branch) {
    envFormErrors.branch = "请输入默认分支";
    isValid = false;
  }

  return isValid;
};

const handleConfirm = async () => {
  if (!validateEnvForm()) return;

  submitting.value = true;
  let api = props.envData?.id ? updateEnvironment : addEnvironment;
  let data = {
    ...props.envData,
    ...envForm,
  };
  try {
    // @ts-ignore
    await api(data);
    ElMessage.success(props.envData?.id ? "环境编辑成功" : "环境添加成功");
    emit("success");
    emit("update:visible", false);

    envForm.name = "";
    envForm.eName = "";
    envForm.branch = "";
    envForm.envMaps = [{ key: "", value: "" }];
  } catch (error: any) {
    ElMessage.error(
      error || (props.envData?.id ? "编辑环境失败" : "添加环境失败"),
    );
  } finally {
    submitting.value = false;
  }
};
</script>
